import { exerciseKey, isKnownName, literalKey, resolveName } from "./exerciseKey.js";
import { dayIn, isDateHeading } from "./headingDate.js";
import { type Block, type Exercise, type Plan, type Target, exercise as makeExercise, newID } from "./models.js";
import { startOfDay } from "./time.js";
import {
  B,
  D,
  S,
  chars,
  count,
  dropLast,
  first,
  hasLetter,
  isASCII,
  isLetter,
  isLowercase,
  isNumber,
  isWhitespace,
  splitNewlines,
  splitOmitting,
  splitSpaces,
  toInt,
  trimSet,
  trimWS,
} from "./text.js";
import type { LineKind, LineReading, TargetUnit } from "./types.js";

// WeightsCore's `RuleParser` (with `Supersets`, `HeadingDate` and `PlanSection.isSessionHeading`),
// line for line. The shapes it reads are DESIGN.md §1.

export interface UnreadLine {
  text: string;
  position: number;
  joinsBlock: boolean;
  header: string | null;
  indexInBlock: number;
  warning: string;
}

export interface ParseResult {
  plan: Plan;
  warnings: string[];
  confidence: number;
  unreadLines: UnreadLine[];
  /** Every line behind a `Couldn’t read` warning, in order. */
  unread: string[];
  /** Every line of the text, in order, with what the rules made of it. */
  lines: LineReading[];
}

export interface ParseOptions {
  knownExercises?: readonly string[];
  /** Epoch milliseconds. */
  now?: number;
  timeZone?: string;
  notBefore?: number;
}

export function couldNotRead(line: string): string {
  return `Couldn’t read “${line}”`;
}

interface Member {
  exercise: Exercise;
  sets: number | null;
  ownLine: boolean;
}

interface Header {
  sets: number | null;
  note: string | null;
  remainder: string;
  isSuperset: boolean;
}

interface Draft {
  sets: number | null;
  note: string | null;
  defaultTarget: Target | null;
  source: string;
  inheritsTarget: boolean;
  wantsCount: boolean;
  letter: string | null;
  implicit: boolean;
  prep: boolean;
  bulletedList: boolean;
  lastIndent: number | null;
  members: Member[];
  /** The line that opened the block. */
  opener: LineRecord;
  /** Every line that went into the block, given its id when it is kept. */
  lines: LineRecord[];
}

/** What one (mended) line became, filled in as the parser decides it. */
interface LineRecord {
  kind: LineKind;
  exerciseIds: string[];
  blockId: string | null;
}

function cloneMember(member: Member): Member {
  return { exercise: { ...member.exercise }, sets: member.sets, ownLine: member.ownLine };
}

const noteLimit = 200;

class State {
  known: string[];
  blocks: Block[] = [];
  draft: Draft | null = null;
  title: string | null = null;
  warnings: string[] = [];
  unread: string[] = [];
  nonBlankLines = 0;
  understoodLines = 0;
  prep = false;
  sectionBullets = false;
  sectionIndent: number | null = null;
  unreadLines: UnreadLine[] = [];
  current: LineRecord = { kind: "unread", exerciseIds: [], blockId: null };

  constructor(known: readonly string[]) {
    this.known = known.slice();
  }

  get listIsOpen(): boolean {
    return this.draft !== null && !this.draft.implicit && this.draft.letter === null;
  }

  get isEmptySoFar(): boolean {
    return this.blocks.length === 0 && this.draft === null;
  }

  canTakeLooseName(body: string, indent: number, bulleted: boolean): boolean {
    const draft = this.draft;
    if (draft === null || draft.implicit) return false;
    const words = splitSpaces(body);
    if (!(words.length <= 5 && !body.includes(":") && !body.endsWith("."))) return false;
    const found = member(body, null, this.known);
    if (found !== null && isKnownName(found.exercise.name, this.known)) return true;
    if (draft.bulletedList && !bulleted) return false;
    if (draft.lastIndent !== null && indent >= draft.lastIndent + 2) return false;
    return true;
  }

  get holdsAnExercise(): boolean {
    return this.draft !== null && this.draft.members.length > 0;
  }

  attachCue(cue: string): boolean {
    const draft = this.draft;
    if (draft === null || draft.members.length === 0) return false;
    const target = draft.members[draft.members.length - 1]!;
    const text = trimWS(cue);
    const existing = target.exercise.note;
    const joined = existing !== null ? `${existing} · ${text}` : text;
    if (count(joined) > noteLimit) return false;
    target.exercise.note = joined;
    this.current.kind = "note";
    this.current.exerciseIds = [target.exercise.id];
    draft.lines.push(this.current);
    return true;
  }

  members(body: string): Member[] {
    return members(body, this.draft?.defaultTarget ?? null, this.known);
  }

  warn(line: string): void {
    this.warnings.push(couldNotRead(line));
    this.unread.push(line);
  }

  /** The current line produced these exercises (or, with none, set up a block) in the open draft. */
  produced(list: readonly Member[]): void {
    this.current.kind = list.length > 0 ? "exercise" : "block";
    this.current.exerciseIds = list.map((m) => m.exercise.id);
    this.draft?.lines.push(this.current);
  }

  unreadLine(line: string): void {
    this.current.kind = "unread";
    const inside = this.draft !== null ? !this.draft.implicit : false;
    this.unreadLines.push({
      text: line,
      position: this.blocks.length + (this.draft !== null && !inside ? 1 : 0),
      joinsBlock: inside,
      header: inside ? (this.draft?.source ?? null) : null,
      indexInBlock: inside ? (this.draft?.members.length ?? 0) : 0,
      warning: couldNotRead(line),
    });
    this.warn(line);
  }

  openHeader(header: Header, source: string): void {
    const opened: Draft = {
      sets: header.sets,
      note: header.note,
      defaultTarget: null,
      source,
      inheritsTarget: header.isSuperset,
      wantsCount: header.isSuperset && header.sets === null,
      letter: null,
      implicit: false,
      prep: this.prep,
      bulletedList: false,
      lastIndent: null,
      members: [],
      opener: this.current,
      lines: [],
    };
    if (header.remainder !== "") {
      const parsed = members(header.remainder, null, this.known);
      if (parsed.length === 0) {
        opened.defaultTarget = leadingTarget(header.remainder).target;
      } else {
        opened.members = parsed;
        opened.inheritsTarget = opened.inheritsTarget || parsed.length > 1;
        opened.implicit = true;
        for (const m of parsed) this.known.unshift(m.exercise.name);
      }
    }
    this.draft = opened;
  }

  openMembers(list: Member[], sets: number | null, source: string): void {
    this.draft = {
      sets,
      note: null,
      defaultTarget: null,
      source,
      inheritsTarget: list.length > 1,
      wantsCount: false,
      letter: null,
      implicit: true,
      prep: this.prep,
      bulletedList: false,
      lastIndent: null,
      members: [],
      opener: this.current,
      lines: [],
    };
    this.append(list);
  }

  addLabelled(list: Member[], letter: string, source: string, indent: number): void {
    if (this.draft?.letter !== letter) {
      this.flush();
      this.draft = {
        sets: null,
        note: null,
        defaultTarget: null,
        source,
        inheritsTarget: true,
        wantsCount: false,
        letter,
        implicit: false,
        prep: this.prep,
        bulletedList: false,
        lastIndent: null,
        members: [],
        opener: this.current,
        lines: [],
      };
    }
    this.append(list, indent, false);
  }

  add(list: Member[], source: string, ownBlock: boolean, indent: number, bulleted: boolean): void {
    if (this.draft === null || (ownBlock && this.draft.implicit)) {
      this.flush();
      this.draft = {
        sets: 1,
        note: null,
        defaultTarget: null,
        source,
        inheritsTarget: false,
        wantsCount: false,
        letter: null,
        implicit: true,
        prep: this.prep,
        bulletedList: false,
        lastIndent: null,
        members: [],
        opener: this.current,
        lines: [],
      };
    }
    this.append(
      list.map((m) => ({ ...cloneMember(m), ownLine: true })),
      indent,
      bulleted,
    );
  }

  private append(list: Member[], indent: number | null = null, bulleted = false): void {
    const draft = this.draft;
    if (draft === null) return;
    if (draft.members.length === 0 && indent !== null) draft.bulletedList = bulleted;
    if (indent !== null) draft.lastIndent = indent;
    draft.members.push(...list);
    for (const m of list) this.known.unshift(m.exercise.name);
  }

  flush(): void {
    const draft = this.draft;
    if (draft === null) return;
    this.draft = null;
    if (draft.members.length === 0) {
      draft.opener.kind = "unread";
      draft.opener.exerciseIds = [];
      for (const record of draft.lines) record.blockId = null;
      this.warn(draft.source);
      this.understoodLines -= 1;
      return;
    }

    const exercises = draft.members.map((m) => ({ ...m.exercise }));
    if (draft.sets !== null && !draft.implicit) {
      draft.members.forEach((m, index) => {
        if (m.ownLine && m.sets !== null && m.sets > 1) exercises[index]!.groups = m.sets;
      });
    }
    const inherited = exercises[0]?.target ?? null;
    if (draft.inheritsTarget && inherited !== null) {
      for (const e of exercises) if (e.target === null) e.target = inherited;
    }

    const declared = draft.members.map((m) => m.sets).filter((s): s is number => s !== null);
    let sets: number;
    if (draft.sets !== null) {
      sets = draft.sets;
    } else if (declared.length > 0) {
      sets = declared[0]!;
      if (new Set(declared).size > 1) {
        const largest = Math.max(...declared);
        const names = exercises.map((e) => e.name).join(" / ");
        this.warnings.push(`“${names}” disagree on how many sets — using ${largest}`);
        sets = largest;
      }
    } else {
      sets = 1;
      if (draft.wantsCount) this.warnings.push(`“${draft.source}” doesn’t say how many rounds — using 1`);
    }
    const id = newID();
    for (const record of draft.lines) record.blockId = id;
    this.blocks.push({ id, sets: Math.max(1, sets), note: draft.note, exercises, isPrep: draft.prep });
  }
}

export function parse(text: string, options: ParseOptions = {}): ParseResult {
  const now = options.now ?? Date.now();
  const timeZone = options.timeZone ?? "UTC";
  const state = new State(options.knownExercises ?? []);

  const physical = splitNewlines(text);
  const mended = mendLayoutRanges(physical);
  const records: LineRecord[] = [];
  for (const { text: rawLine } of mended) {
    const record: LineRecord = { kind: "unread", exerciseIds: [], blockId: null };
    records.push(record);
    state.current = record;
    const indent = indentation(rawLine);
    const line = trimWS(rawLine);
    if (line === "") {
      record.kind = "blank";
      state.flush();
      state.prep = false;
      state.sectionBullets = false;
      state.sectionIndent = null;
      continue;
    }
    state.nonBlankLines += 1;
    let { body, hadBullet } = stripListMarker(line);
    if (hadBullet && isBareGroupHeader(body)) {
      hadBullet = false;
    } else if (hadBullet && state.listIsOpen && !ownsItsSets(body)) {
      // A plain bullet under an open `3 sets` belongs to it, however it was indented.
    } else if (hadBullet && !state.listIsOpen && ownsItsSets(body)) {
      hadBullet = false;
      state.flush();
    } else if (hadBullet && state.sectionBullets) {
      const base = state.sectionIndent ?? indent;
      state.sectionIndent = base;
      if (indent <= base) {
        hadBullet = false;
        state.flush();
      }
    } else if (!hadBullet) {
      state.sectionBullets = false;
    }
    if (body === "") {
      state.unreadLine(line);
      continue;
    }

    if (!hadBullet) {
      const found = label(body);
      if (found !== null) {
        const list = state.members(found.rest);
        if (list.length > 0) {
          state.addLabelled(list, found.letter, line, indent);
          state.produced(list);
          state.understoodLines += 1;
          continue;
        }
      }
    }

    if (!hadBullet && isPrepHeader(body)) {
      state.flush();
      state.prep = true;
      state.sectionBullets = body.endsWith(":");
      state.sectionIndent = null;
      record.kind = "heading";
      state.understoodLines += 1;
      continue;
    }

    if (!hadBullet && isMainHeader(body)) {
      state.flush();
      state.prep = false;
      state.sectionBullets = true;
      state.sectionIndent = null;
      record.kind = "heading";
      state.understoodLines += 1;
      continue;
    }

    if (!hadBullet) {
      const nested = nestedDistance(body);
      if (nested !== null) {
        const found = member(nested.rest, null, state.known);
        if (found !== null) {
          state.flush();
          state.openMembers(copies(found, nested.reps), nested.sets, line);
          state.produced(state.draft!.members);
          state.understoodLines += 1;
          continue;
        }
      }
    }

    if (!hadBullet) {
      const found = group(body);
      if (found !== null) {
        const list = state.members(found.content).flatMap(distanceCopies);
        if (list.length > 0) {
          state.flush();
          state.openMembers(list, found.sets, line);
          state.draft!.note = found.note;
          state.produced(state.draft!.members);
          state.understoodLines += 1;
          continue;
        }
      }
    }

    if (!hadBullet) {
      const found = supersetHeader(body) ?? header(body);
      if (found !== null && state.members(found.remainder).length <= 1) {
        state.flush();
        state.openHeader(found, line);
        state.produced(state.draft!.members);
        state.understoodLines += 1;
        continue;
      }
    }

    if (!hadBullet) {
      const list = state.members(body);
      if (list.length > 1 || (list.length === 1 && list[0]!.sets !== null)) {
        state.flush();
        state.openMembers(list, null, line);
        state.produced(state.draft!.members);
        state.understoodLines += 1;
        continue;
      }
    }

    const startsWithNumber = isNumber(first(body));
    if (!hadBullet && startsWithNumber && state.title === null && state.isEmptySoFar && isDateHeading(body, now, timeZone)) {
      state.title = body;
      record.kind = "heading";
      state.understoodLines += 1;
      continue;
    }

    if (hadBullet && !startsWithNumber && body.includes(":") && state.holdsAnExercise && state.attachCue(body)) {
      state.understoodLines += 1;
      continue;
    }

    if (hadBullet || startsWithNumber || state.canTakeLooseName(body, indent, hadBullet)) {
      const list = state.members(body);
      if (list.length > 0) {
        state.add(list, line, !hadBullet, indent, hadBullet);
        state.produced(list);
        state.understoodLines += 1;
        continue;
      }
    }

    if (!hadBullet && state.holdsAnExercise && isCue(body, state.known, now, timeZone)) {
      if (state.attachCue(body)) {
        state.understoodLines += 1;
      } else {
        state.unreadLine(line);
      }
      continue;
    }

    if (state.title === null && state.isEmptySoFar) {
      state.title = body;
      record.kind = "heading";
      state.understoodLines += 1;
    } else {
      state.unreadLine(line);
    }
  }
  state.flush();

  const heading = state.title;
  const named = heading === null ? null : dayIn(heading, options.notBefore ?? now, now, timeZone);
  const plan: Plan = {
    id: newID(),
    title: named?.remainder ?? heading ?? "",
    date: named?.date ?? startOfDay(now, timeZone),
    createdAt: now,
    sourceText: text,
    interpretedBy: "rules",
    blocks: state.blocks,
    alsoToday: [],
    teamSessionID: null,
  };
  const confidence = state.nonBlankLines === 0 ? 0 : Math.max(0, state.understoodLines) / state.nonBlankLines;
  const lines = lineReadings(physical, mended, records, state.blocks);
  return { plan, warnings: state.warnings, confidence, unreadLines: state.unreadLines, unread: state.unread, lines };
}

/**
 * Each physical line with its mended line's reading. A line `mendLayout` joined onto the one above
 * is part of that line: a note of its exercises, or the same heading, block or unread line.
 */
function lineReadings(
  physical: string[],
  mended: MendedLine[],
  records: LineRecord[],
  blocks: Block[],
): LineReading[] {
  const checked = new Set(blocks.flatMap((b) => b.exercises.filter((e) => e.needsCheck).map((e) => e.id)));
  const out: LineReading[] = [];
  mended.forEach((range, n) => {
    const record = records[n]!;
    let kind = record.kind;
    let ids = record.exerciseIds;
    if (kind === "exercise" && ids.some((id) => checked.has(id))) {
      kind = "unread";
      ids = ids.filter((id) => checked.has(id));
    }
    for (let offset = 0; offset < range.count; offset += 1) {
      const index = range.first + offset;
      const reading: LineReading = {
        index,
        text: physical[index]!,
        kind: offset > 0 && kind === "exercise" ? "note" : kind,
        exerciseIds: offset === 0 ? ids.slice() : kind === "exercise" ? ids.slice(-1) : [],
      };
      if (record.blockId !== null) reading.blockId = record.blockId;
      out.push(reading);
    }
  });
  return out;
}

const bullets = new Set(["-", "•", "*", "–", "—", "·", "▪", "‣", "+"]);

export function stripListMarker(line: string): { body: string; hadBullet: boolean } {
  let rest = chars(line);
  let hadBullet = false;
  while (rest.length > 0 && bullets.has(rest[0]!)) {
    rest = rest.slice(1);
    hadBullet = true;
  }
  let i = 0;
  while (i < rest.length && isWhitespace(rest[i])) i += 1;
  rest = rest.slice(i);

  let digits = 0;
  while (digits < rest.length && isNumber(rest[digits])) digits += 1;
  if (digits > 0 && digits <= 2) {
    const marker = rest[digits];
    if ((marker === "." || marker === ")") && isWhitespace(rest[digits + 1])) {
      let j = digits + 1;
      while (j < rest.length && isWhitespace(rest[j])) j += 1;
      rest = rest.slice(j);
      hadBullet = true;
    }
  }
  return { body: trimWS(rest.join("")), hadBullet };
}

export function label(body: string): { letter: string; rest: string } | null {
  const cs = chars(body);
  const head = cs[0];
  if (head === undefined || !isLetter(head) || !isASCII(head)) return null;
  if (head === "x" || head === "X") return null;

  let index = 1;
  if (index < cs.length && cs[index] === "-" && index + 1 < cs.length && isNumber(cs[index + 1])) index += 1;
  const digitsStart = index;
  while (index < cs.length && isNumber(cs[index])) index += 1;
  const hasDigits = index > digitsStart;
  let hasTerminator = false;
  if (index < cs.length && (cs[index] === "." || cs[index] === ")" || cs[index] === ":")) {
    hasTerminator = true;
    index += 1;
  }
  if (!((hasDigits || hasTerminator) && index < cs.length && isWhitespace(cs[index]))) return null;

  const rest = trimWS(cs.slice(index).join(""));
  const letter = first(head.toUpperCase());
  if (rest === "" || letter === undefined) return null;
  return { letter, rest };
}

const multiplication = new Set(["x", "X", "×", "*"]);
const roundWords = new Set(["set", "sets", "round", "rounds", "rd", "rds", "circuit", "circuits"]);
const groupWords = new Set(["set", "sets", "round", "rounds", "series"]);

function leadingWord(text: string[]): string | null {
  let word = "";
  for (const c of text) {
    if (!isLetter(c)) break;
    word += c;
  }
  return word === "" ? null : word;
}

function dropWS(text: string[]): string[] {
  let i = 0;
  while (i < text.length && isWhitespace(text[i])) i += 1;
  return text.slice(i);
}

function prefixDigits(text: string[]): string {
  let out = "";
  for (const c of text) {
    if (!isNumber(c)) break;
    out += c;
  }
  return out;
}

export function supersetHeader(body: string): Header | null {
  let rest = chars(body);
  let sets: number | null = null;

  const leadingDigits = prefixDigits(rest);
  if (leadingDigits !== "" && count(leadingDigits) <= 2) {
    sets = toInt(leadingDigits);
    rest = dropWS(rest.slice(count(leadingDigits)));
  }

  const word = leadingWord(rest);
  if (word === null) return null;
  const lowered = word.toLowerCase();
  let consumed = count(word);
  if (lowered === "s" && rest.slice(count(word)).join("").toLowerCase().startsWith("/s")) {
    consumed += 2;
  } else if (!["superset", "supersets", "ss"].includes(lowered)) {
    return null;
  }
  rest = dropWS(rest.slice(consumed));
  if (rest[0] === ":") rest = rest.slice(1);
  rest = dropWS(rest);

  if (rest[0] !== undefined && multiplication.has(rest[0])) rest = dropWS(rest.slice(1));
  const countDigits = prefixDigits(rest);
  if (countDigits !== "" && count(countDigits) <= 2) {
    sets = toInt(countDigits);
    rest = dropWS(rest.slice(count(countDigits)));
  }

  const { note: bracket, rest: remainder } = splitNote(rest.join(""));
  if (remainder !== "") return null;
  let note = bracket;
  if (bracket !== null) {
    const rounds = countIn(bracket);
    if (rounds !== null) {
      sets = sets ?? rounds;
      note = null;
    }
  }
  return { sets, note, remainder: "", isSuperset: true };
}

export function header(body: string): Header | null {
  let rest = chars(body);
  const digits = prefixDigits(rest);
  if (digits === "" || count(digits) > 2) return null;
  let sets = toInt(digits);
  if (sets === null) return null;
  rest = rest.slice(count(digits));
  let range: string | null = null;
  if (rest[0] === "-" || rest[0] === "–") {
    const upper = prefixDigits(rest.slice(1));
    const value = toInt(upper);
    if (value !== null && value > sets && count(upper) <= 2) {
      const afterUpper = dropWS(rest.slice(1 + count(upper)));
      const word = leadingWord(afterUpper);
      if (word !== null && roundWords.has(word.toLowerCase())) {
        range = `${sets}-${value} ${word.toLowerCase()}`;
        sets = value;
        rest = rest.slice(1 + count(upper));
      }
    }
  }
  const afterDigits = dropWS(rest);

  const word = leadingWord(afterDigits);
  if (afterDigits[0] !== undefined && multiplication.has(afterDigits[0])) {
    rest = afterDigits.slice(1);
  } else if (word !== null && roundWords.has(word.toLowerCase())) {
    rest = afterDigits.slice(count(word));
  } else {
    return null;
  }

  rest = dropWS(rest);
  if (rest[0] === ":") rest = rest.slice(1);
  rest = dropWS(rest);
  const of = leadingWord(rest);
  if (of !== null && of.toLowerCase() === "of") rest = dropWS(rest.slice(count(of)));

  const raw = trimWS(rest.join(""));
  const { note, rest: withoutNote } = splitNote(raw);
  if (withoutNote === "") {
    const joined = [range, note].filter((s): s is string => s !== null).join(" · ");
    return { sets: Math.max(1, sets), note: joined === "" ? null : joined, remainder: "", isSuperset: false };
  }
  return { sets: Math.max(1, sets), note: range, remainder: raw, isSuperset: false };
}

function countIn(bracket: string): number | null {
  let rest = chars(trimWS(bracket));
  if (rest[0] !== undefined && multiplication.has(rest[0])) rest = rest.slice(1);
  const digits = prefixDigits(rest);
  if (digits === "" || count(digits) > 2) return null;
  const value = toInt(digits);
  if (value === null) return null;
  rest = dropWS(rest.slice(count(digits)));
  if (rest.length === 0) return value;
  const word = leadingWord(rest);
  if (word === null || !roundWords.has(word.toLowerCase())) return null;
  if (!rest.slice(count(word)).every(isWhitespace)) return null;
  return value;
}

export function members(body: string, defaultTarget: Target | null, known: readonly string[]): Member[] {
  return supersetParts(body, known)
    .map((part) => member(part, defaultTarget, known))
    .filter((m): m is Member => m !== null);
}

const bodyweightWords = new Set([
  "jump", "jumping", "hop", "bound", "pogo", "skip", "burpee", "crunch", "plank", "dip", "nordic",
  "bridge", "skydiver", "bodyweight",
]);
const bodyweightPairs = new Set([
  "push up", "pull up", "chin up", "sit up", "mountain climber", "dead bug", "knee raise", "leg raise",
]);
const loadWords = new Set([
  "mb", "medicine", "ball", "dumbbell", "kettlebell", "bar", "barbell", "trap", "weight", "weights", "weighted",
  "vest", "plate", "sandbag", "sled", "kg", "lb", "lbs", "goblet", "landmine", "cable", "machine",
  "shrug", "press", "clean", "snatch", "jerk", "load",
]);

const negations = new Set(["no", "without"]);

function isNegated(words: readonly string[], index: number): boolean {
  const before = words.slice(Math.max(0, index - 2), index);
  const last = before.at(-1);
  if (last === undefined) return false;
  if (negations.has(last)) return true;
  return (last === "a" || last === "any") && before.length === 2 && negations.has(before[0]!);
}

/** `no weight`, `without a bar` or `bodyweight` anywhere on the line say so outright, whatever the move is called. */
export function isBodyweightMove(name: string, line: string): boolean {
  const words = splitSpaces(exerciseKey(name));
  const pairs = words.slice(1).map((w, i) => `${words[i]} ${w}`);
  const said = splitSpaces(exerciseKey(line));
  let loaded = false;
  let unloaded = said.includes("bodyweight");
  said.forEach((w, i) => {
    if (!(loadWords.has(w) || (w.endsWith("kg") && chars(dropLast(w, 2)).every(isNumber)))) return;
    if (isNegated(said, i)) unloaded = true;
    else loaded = true;
  });
  if (loaded) return false;
  return unloaded || words.some((w) => bodyweightWords.has(w)) || pairs.some((p) => bodyweightPairs.has(p));
}

const liftWords = new Set(["deadlift", "squat", "press", "clean", "snatch", "jerk", "row", "thrust", "lunge", "curl"]);
const holdWords = new Set(["hold", "iso", "isometric", "static"]);

/**
 * A lift held for time (DESIGN.md §9.1): a lift's name and a word saying it is held, so
 * `30s wall squat` and a timed `bench press` stay timed.
 */
export function isLoadedHold(name: string, line: string): boolean {
  const said = splitSpaces(exerciseKey(line));
  return splitSpaces(exerciseKey(name)).some((w) => liftWords.has(w)) && said.some((w) => holdWords.has(w)) && !isBodyweightMove(name, line);
}

const nameTrim = " \t-–—:,;.";

export function member(body: string, defaultTarget: Target | null, known: readonly string[]): Member | null {
  let text = trimWS(body);
  let perSide = false;
  if (text.endsWith("*")) {
    perSide = true;
    text = trimWS(dropLast(text));
  }
  const split = splitNote(text);
  const note = split.note;
  text = split.rest;
  if (text.endsWith("*")) {
    perSide = true;
    text = trimWS(dropLast(text));
  }
  const hurdled = withoutHurdleMark(text);
  if (hurdled !== null) text = hurdled;

  let sets: number | null = null;
  let target: Target | null = null;
  let rest = text;
  const leading = leadingSetsAndReps(text);
  if (leading !== null) {
    sets = leading.sets;
    target = leading.target;
    rest = leading.rest;
  } else {
    const lead = leadingTarget(text);
    if (lead.target !== null) {
      target = lead.target;
      rest = lead.rest;
    } else {
      const trailing = trailingSetsAndReps(text);
      if (trailing !== null) {
        sets = trailing.sets;
        target = trailing.target;
        rest = trailing.rest;
      }
    }
  }

  let name = trimSet(rest, nameTrim);
  if (name.includes("*")) {
    const words = name.split(" ").map((word) => {
      if (word.endsWith("*")) {
        perSide = true;
        return dropLast(word);
      }
      return word;
    });
    name = trimWS(words.join(" "));
  }
  const lifted = cuesOutOfName(name);
  name = lifted.name;
  let cues = lifted.cues;
  if (hurdled !== null) {
    name = hurdleName(name);
    cues = cues.flatMap(hurdleCues);
  }
  if (target?.unit === "meters" && (!hasLetter(name) || isFiller(exerciseKey(name)))) {
    if (hasLetter(name)) cues.push(name);
    name = runName([body, ...cues, ...(note !== null ? [note] : [])].join(" "));
  }
  if (!hasLetter(name) || isFiller(exerciseKey(name)) || isSessionReference(name)) return null;
  const noteWords = note === null ? null : splitOmitting(note.toLowerCase(), (c) => !isLetter(c));
  const lead = cues.slice(0, 1).filter((word) => !(noteWords?.includes(word) ?? false));
  const notes = [...lead, ...(note !== null ? [note] : []), ...cues.slice(1)];

  const resolved = resolveName(sentenceCased(name), known);
  // `2x4s static iso-hold deadlift`: a bar is held, so its weight is logged, with the holds as reps.
  if (target?.unit === "seconds" && isLoadedHold(resolved, body)) {
    const time = target.upper !== null ? `${target.amount}-${target.upper}` : `${target.amount}`;
    return {
      exercise: makeExercise({
        name: resolved,
        target: { amount: sets ?? 1, upper: null, unit: "reps" },
        note: [`${time} s hold`, ...notes].join(" · "),
        perSide,
        isBodyweight: false,
      }),
      sets: null,
      ownLine: false,
    };
  }
  return {
    exercise: makeExercise({
      name: resolved,
      target: target ?? defaultTarget,
      note: notes.length === 0 ? null : notes.join(" · "),
      perSide,
      isBodyweight: isBodyweightMove(resolved, body),
    }),
    sets,
    ownLine: false,
  };
}

const hurdleMark = new RegExp(String.raw`^(?:${D}{1,2}${S}*[xX×]${S}*)?${D}{1,3}H${B}`, "u");

/**
 * `8H`, `4x8H`: a count of hurdles (DESIGN.md §19.1), spelled out as `4x8 hurdles …`, so the count reads as hurdles
 * a rep and his words after it as the name.
 */
export function withoutHurdleMark(text: string): string | null {
  const mark = hurdleMark.exec(text);
  if (mark === null) return null;
  return trimWS(`${dropLast(mark[0])} hurdles ${text.slice(mark[0].length)}`);
}

const spelledHurdles = new RegExp(String.raw`^hurdles${B}`, "u");

const hurdleNouns = new Set(["mobility", "drill", "drills"]);

/** His words after `NH` name it: `6H lateral A skip` → Hurdles lateral A skip, `5H Mobility` → Hurdle mobility. */
export function hurdleName(name: string): string {
  const words = trimWS(name.replace(spelledHurdles, ""));
  const first = words.split(" ").find((w) => w !== "");
  if (first === undefined) return "Hurdles";
  if (hurdleNouns.has(words.toLowerCase())) return `Hurdle ${words.toLowerCase()}`;
  const said = first === first.toUpperCase() ? words : words.slice(0, 1).toLowerCase() + words.slice(1);
  return `Hurdles ${said}`;
}

const atPrefix = new RegExp(String.raw`^@${S}*`, "u");

/** `@ 6-8 discount - low - R: 2-3min` on hurdles is three cues: `6-8 discount`, `low`, `rest 2-3min`. */
export function hurdleCues(cue: string): string[] {
  let pieces = [cue.replace(atPrefix, "")];
  for (const dash of [" - ", " – ", " — "]) pieces = pieces.flatMap((p) => p.split(dash));
  return pieces.map((p) => trimWS(p).replace(restPrefix, "rest ")).filter((p) => p !== "");
}

const intensityWords = new Set([
  "light", "heavy", "fast", "slow", "easy", "moderate", "explosive", "medium",
  "alternating", "alternate", "stopped", "paused", "pause",
]);
const runWords = new Set(["tempo", "sprint", "sprints", "accel", "accels", "run", "runs"]);
const placePhrases = [
  "in place", "on the spot",
  "on grass", "on the grass", "on turf", "on the turf", "on the track", "on track", "on sand",
];

const restCue = new RegExp(String.raw`${S}+R${S}*:.*$`, "u");
const restPrefix = new RegExp(String.raw`^R${S}*:${S}*`, "u");
const effortCue = new RegExp(String.raw`${S}+to${S}+${D}{2,3}${S}*%.*$`, "iu");
const withCue = new RegExp(String.raw` with (?=${D})`, "iu");

export function cuesOutOfName(input: string): { name: string; cues: string[] } {
  let name = input;
  let leading: string | null = null;
  let trailing: string[] = [];

  const at = name.indexOf("@");
  if (at >= 0) {
    const cue = trimWS(name.slice(at));
    name = name.slice(0, at);
    if (count(cue) > 1) trailing.push(cue);
  }
  for (const dash of [" - ", " – ", " — "]) {
    const index = name.indexOf(dash);
    if (index >= 0) {
      const cue = trimWS(name.slice(index + dash.length));
      name = name.slice(0, index);
      if (hasLetter(cue)) trailing.push(cue);
      break;
    }
  }
  const rest = restCue.exec(name);
  if (rest !== null) {
    trailing.push(trimWS(rest[0]).replace(restPrefix, "rest "));
    name = name.slice(0, rest.index);
  }
  const effort = effortCue.exec(name);
  if (effort !== null) {
    trailing.unshift(trimWS(effort[0]));
    name = name.slice(0, effort.index);
  }
  const withMatch = withCue.exec(name);
  if (withMatch !== null) {
    trailing.push(name.slice(withMatch.index + 1));
    name = name.slice(0, withMatch.index);
  }
  for (const phrase of placePhrases) {
    if (name.toLowerCase().endsWith(" " + phrase)) {
      name = dropLast(name, count(phrase) + 1);
      trailing.push(phrase);
      break;
    }
  }
  const words = splitSpaces(name);
  if (words.length >= 3 && words[0]!.toLowerCase() === "very" && intensityWords.has(words[1]!.toLowerCase())) {
    words[1] = "very " + words[1];
    words.shift();
  }
  const isRun = words.length === 2 && runWords.has(words[1]!.toLowerCase());
  if (words.length >= 3 || isRun) {
    const lastWord = splitSpaces(words[0]!).pop()?.toLowerCase() ?? "";
    if (intensityWords.has(lastWord)) {
      leading = words.shift()!.toLowerCase();
      name = words.join(" ");
    }
  }
  trailing = trailing.map((cue) => cue.replace(restPrefix, "rest "));
  const cues = [...(leading !== null ? [leading] : []), ...trailing];
  return { name: trimSet(name, nameTrim), cues };
}

export function runName(text: string): string {
  const words = new Set(splitOmitting(text.toLowerCase(), (c) => !isLetter(c)));
  const table: [string, string][] = [
    ["sprint", "Sprint"], ["sprints", "Sprint"], ["tempo", "Tempo"],
    ["accel", "Accel"], ["acceleration", "Accel"], ["accels", "Accel"],
  ];
  for (const [word, name] of table) if (words.has(word)) return name;
  return "Run";
}

const fillerPhrases = new Set([
  "for time", "each side", "per side", "each leg", "per leg", "each arm", "per arm",
  "each", "total", "of", "amrap", "emom", "superset", "circuit", "warm up", "cool down",
]);

function isFiller(key: string): boolean {
  const words = splitSpaces(key);
  return (
    fillerPhrases.has(key) ||
    words[0] === "rest" ||
    words[words.length - 1] === "rest" ||
    key.startsWith("between set") ||
    words.includes("recovery")
  );
}

export function indentation(rawLine: string): number {
  let columns = 0;
  for (const c of rawLine) {
    if (c === "\t") columns += 4;
    else if (isWhitespace(c)) columns += 1;
    else break;
  }
  return columns;
}

export function isCue(body: string, known: readonly string[], now: number, timeZone: string): boolean {
  if (splitOmitting(body, isWhitespace).length < 3) return false;
  if (
    label(body) !== null ||
    group(body) !== null ||
    supersetHeader(body) !== null ||
    header(body) !== null ||
    isPrepHeader(body) ||
    isSessionReference(body) ||
    isSessionHeading(body, now, timeZone)
  ) {
    return false;
  }
  if (prescribesWork(body)) return false;
  const found = member(body, null, known);
  if (found === null) return false;
  return found.sets === null && found.exercise.target === null && !isKnownName(found.exercise.name, known);
}

const workCount = new RegExp(String.raw`${B}${D}{1,2}${S}*[xX×]${S}*(?:${D}|\()`, "u");
const workRounds = new RegExp(String.raw`${B}${D}{1,2}${S}*(sets?|rounds?|séries?)${B}`, "iu");

export function prescribesWork(body: string): boolean {
  return workCount.test(body) || workRounds.test(body);
}

const prepWords = new Set(["prep", "prep work", "warm up", "warmup", "activation", "preparation"]);
const mainWords = new Set([
  "main", "main set", "main sets", "main part", "main work", "main workout", "workout", "strength",
]);

export function isPrepHeader(body: string): boolean {
  let text = trimWS(body);
  const announced = text.endsWith(":");
  if (announced) text = trimWS(dropLast(text));
  const key = literalKey(text);
  if (prepWords.has(key)) return true;
  return announced && isSessionReference(text) && splitSpaces(key).includes("prep");
}

export function isMainHeader(body: string): boolean {
  let text = trimWS(body);
  const announced = text.endsWith(":");
  if (announced) text = dropLast(text);
  const key = literalKey(text);
  return announced ? mainWords.has(key) : ["main", "main set", "main sets"].includes(key);
}

const bareGroupHeader = new RegExp(String.raw`^${D}{1,2}${S}*(sets?|rounds?)${S}*:?$`, "iu");
const ownsSets = new RegExp(String.raw`^${D}{1,2}${S}*([xX×*]${S}*(?:${D}|\()|(sets?|rounds?)${B})`, "iu");

export function isBareGroupHeader(body: string): boolean {
  return bareGroupHeader.test(body);
}

export function ownsItsSets(body: string): boolean {
  return ownsSets.test(body);
}

export function mendLayout(lines: string[]): string[] {
  return mendLayoutRanges(lines).map((line) => line.text);
}

interface MendedLine {
  text: string;
  /** The physical lines it was mended from. */
  first: number;
  count: number;
}

function mendLayoutRanges(lines: string[]): MendedLine[] {
  const joined: MendedLine[] = [];
  let carry = 0;
  lines.forEach((line, index) => {
    const trimmed = trimWS(line);
    const continues = !stripListMarker(trimmed).hadBullet && (trimmed.includes(")") || isLowercase(first(trimmed)));
    const last = joined[joined.length - 1];
    if (carry > 0 && trimmed !== "" && continues && last !== undefined) {
      last.text = last.text + " " + trimmed;
      last.count += 1;
      carry = Math.max(0, carry + opens(trimmed));
      return;
    }
    joined.push({ text: line, first: index, count: 1 });
    carry = Math.max(0, opens(trimmed));
  });
  return joined;
}

function opens(text: string): number {
  let n = 0;
  for (const c of text) {
    if (c === "(") n += 1;
    if (c === ")") n -= 1;
  }
  return n;
}

export function isSessionReference(name: string): boolean {
  const hash = name.indexOf("#");
  if (hash < 0) return false;
  const before = name.slice(0, hash);
  const after = trimWS(name.slice(hash + 1));
  return chars(before).every((c) => isLetter(c) || isWhitespace(c)) && after !== "" && chars(after).every(isNumber);
}

const nestedHurdles = new RegExp(
  String.raw`^(${D}{1,2})${S}*[xX×]${S}*(${D}{1,2})${S}*[xX×]${S}*\(${S}*(${D}{1,3}H${B}[^()]*?)${S}*\)${S}*(.*)$`,
  "u",
);
const nested = new RegExp(String.raw`^(${D}{1,2})${S}*[xX×]${S}*(${D}{1,2})${S}*[xX×]${S}*(${D}{1,4}(?:${S}*m|H)?${B}.*)$`, "u");

/**
 * `2x4x120m light tempo`, `2x4x15 banana hurdles`, `2x2x5H`: sets, reps in each, and the rest of the line as one rep.
 * A bracket of hurdles is the rep: `2x4x(6H @ 6-8 discount) - R: 6min` is one of `6H @ 6-8 discount - R: 6min`.
 */
export function nestedDistance(body: string): { sets: number; reps: number; rest: string } | null {
  const bracket = nestedHurdles.exec(body);
  if (bracket !== null) {
    const sets = toInt(bracket[1]!);
    const reps = toInt(bracket[2]!);
    if (sets !== null && reps !== null && sets > 0 && reps > 0) {
      return { sets, reps, rest: [bracket[3]!, bracket[4]!].filter((p) => p !== "").join(" ") };
    }
  }
  const match = nested.exec(body);
  if (match === null) return null;
  const sets = toInt(match[1]!);
  const reps = toInt(match[2]!);
  if (sets === null || reps === null || sets <= 0 || reps <= 0) return null;
  return { sets, reps, rest: match[3]! };
}

const meters = new RegExp(String.raw`(${D}{1,4})${S}*m${B}`, "u");

function distanceCopies(m: Member): Member[] {
  const e = m.exercise;
  const target = e.target;
  if (target === null || target.unit !== "reps" || target.amount < 2 || target.amount > 12 || e.note === null) return [m];
  const match = meters.exec(e.note);
  const value = match === null ? null : toInt(match[1]!);
  if (value === null) return [m];
  const run = cloneMember(m);
  run.exercise.target = { amount: value, upper: null, unit: "meters" };
  return copies(run, target.amount);
}

function copies(m: Member, n: number): Member[] {
  return Array.from({ length: Math.max(1, n) }, () => {
    const copy = cloneMember(m);
    copy.exercise.id = newID();
    return copy;
  });
}

export function group(body: string): { sets: number; content: string; note: string | null } | null {
  const cs = chars(body);
  let index = 0;
  while (index < cs.length && isNumber(cs[index])) index += 1;
  if (index === 0 || index > 2) return null;
  const sets = toInt(cs.slice(0, index).join(""));
  if (sets === null) return null;

  while (index < cs.length && cs[index] === " ") index += 1;
  let wordForm = false;
  if (index < cs.length && multiplication.has(cs[index]!)) {
    index += 1;
  } else {
    const word = leadingWord(cs.slice(index)) ?? "";
    if (!groupWords.has(word.toLowerCase())) return null;
    index += count(word);
    wordForm = true;
  }
  while (index < cs.length && cs[index] === " ") index += 1;
  if (index >= cs.length || cs[index] !== "(") return null;
  if (wordForm) {
    const next = cs.slice(index + 1).find((c) => c !== " ");
    if (!isNumber(next)) return null;
  }

  let depth = 0;
  let close = cs.length;
  for (let position = index; position < cs.length; position += 1) {
    if (cs[position] === "(") depth += 1;
    if (cs[position] === ")") {
      depth -= 1;
      if (depth === 0) {
        close = position;
        break;
      }
    }
  }
  const content = cs.slice(index + 1, close).join("");
  if (!hasLetter(content)) return null;
  return { sets: Math.max(1, sets), content, note: trailingNote(cs.slice(close + 1)) };
}

/** `- 3-4min between sets` after a group's bracket: the whole group's cue. */
function trailingNote(cs: string[]): string | null {
  let index = 0;
  while (index < cs.length && (isWhitespace(cs[index]) || "-–—,:;".includes(cs[index]!))) index += 1;
  const note = trimWS(cs.slice(index).join(""));
  return hasLetter(note) ? note : null;
}

export function splitNote(text: string): { note: string | null; rest: string } {
  let note: string | null = null;
  let rest = "";
  let depth = 0;
  let current = "";
  for (const c of text) {
    if (c === "(" || c === "[") {
      depth += 1;
      if (depth === 1) {
        current = "";
        continue;
      }
    }
    if (c === ")" || c === "]") {
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        const trimmed = trimWS(current);
        if (note === null && trimmed !== "") note = trimmed;
        continue;
      }
    }
    if (depth > 0) current += c;
    else rest += c;
  }
  if (depth > 0 && note === null) {
    const trimmed = trimWS(current);
    if (trimmed !== "") note = trimmed;
  }
  return { note, rest: trimWS(splitSpaces(rest).join(" ")) };
}

const unitWords: Record<string, TargetUnit> = {
  s: "seconds", sec: "seconds", secs: "seconds", second: "seconds", seconds: "seconds",
  m: "meters", meter: "meters", meters: "meters", metre: "meters", metres: "meters",
  rep: "reps", reps: "reps",
};
const minuteWords = new Set(["min", "mins", "minute", "minutes"]);

function unitOf(word: string | null): TargetUnit | undefined {
  if (word === null) return undefined;
  const lowered = word.toLowerCase();
  return Object.prototype.hasOwnProperty.call(unitWords, lowered) ? unitWords[lowered] : undefined;
}

export function leadingTarget(body: string): { target: Target | null; rest: string } {
  let rest = chars(body);
  const digits = prefixDigits(rest);
  const amount = toInt(digits);
  if (digits === "" || count(digits) > 3 || amount === null) return { target: null, rest: body };
  rest = rest.slice(count(digits));

  let upper: number | null = null;
  let afterRange = rest;
  let i = 0;
  while (i < afterRange.length && afterRange[i] === " ") i += 1;
  afterRange = afterRange.slice(i);
  const dash = afterRange[0];
  if (dash === "-" || dash === "–" || dash === "—") {
    let afterDash = afterRange.slice(1);
    let j = 0;
    while (j < afterDash.length && afterDash[j] === " ") j += 1;
    afterDash = afterDash.slice(j);
    const upperDigits = prefixDigits(afterDash);
    const value = toInt(upperDigits);
    if (upperDigits !== "" && count(upperDigits) <= 3 && value !== null) {
      upper = value;
      afterRange = afterDash.slice(count(upperDigits));
      rest = afterRange;
    }
  }

  let unit: TargetUnit = "reps";
  let scale = 1;
  const attached = leadingWord(rest);
  const attachedUnit = unitOf(attached);
  if (attached !== null && attachedUnit !== undefined) {
    unit = attachedUnit;
    rest = rest.slice(count(attached));
  } else if (attached !== null && minuteWords.has(attached.toLowerCase())) {
    unit = "seconds";
    scale = 60;
    rest = rest.slice(count(attached));
  } else {
    let k = 0;
    while (k < rest.length && rest[k] === " ") k += 1;
    const spaced = rest.slice(k);
    const word = leadingWord(spaced);
    const spacedUnit = unitOf(word);
    if (word !== null && spacedUnit !== undefined) {
      unit = spacedUnit;
      rest = spaced.slice(count(word));
    } else if (word !== null && minuteWords.has(word.toLowerCase())) {
      unit = "seconds";
      scale = 60;
      rest = spaced.slice(count(word));
    }
  }

  return {
    target: { amount: amount * scale, upper: upper === null ? null : upper * scale, unit },
    rest: trimWS(rest.join("")),
  };
}

export function leadingSetsAndReps(text: string): { sets: number; target: Target; rest: string } | null {
  const cs = chars(text);
  let index = 0;
  while (index < cs.length && isNumber(cs[index])) index += 1;
  if (index === 0 || index > 2) return null;
  const sets = toInt(cs.slice(0, index).join(""));
  if (sets === null) return null;
  while (index < cs.length && cs[index] === " ") index += 1;
  if (index >= cs.length || !multiplication.has(cs[index]!)) return null;
  index += 1;
  const { target, rest } = leadingTarget(trimWS(cs.slice(index).join("")));
  if (target === null) return null;
  return { sets: Math.max(1, sets), target, rest };
}

export function trailingSetsAndReps(text: string): { sets: number; target: Target; rest: string } | null {
  const cs = chars(text);
  let sign = -1;
  for (let i = cs.length - 1; i >= 0; i -= 1) {
    if (multiplication.has(cs[i]!)) {
      sign = i;
      break;
    }
  }
  if (sign < 0) return null;
  let end = sign;
  while (end > 0 && cs[end - 1] === " ") end -= 1;
  let start = end;
  while (start > 0 && isNumber(cs[start - 1])) start -= 1;
  if (!(start > 0 && end > start && end - start <= 2)) return null;
  const sets = toInt(cs.slice(start, end).join(""));
  if (sets === null) return null;
  if (!isWhitespace(cs[start - 1])) return null;
  const { target, rest } = leadingTarget(trimWS(cs.slice(sign + 1).join("")));
  if (target === null || rest !== "") return null;
  const name = trimWS(cs.slice(0, start).join(""));
  if (name === "") return null;
  return { sets: Math.max(1, sets), target, rest: name };
}

export function sentenceCased(name: string): string {
  const shouting = hasLetter(name) && name === name.toUpperCase();
  const base = shouting ? name.toLowerCase() : name;
  const head = first(base);
  if (head === undefined) return base;
  return head.toUpperCase() + chars(base).slice(1).join("");
}

// MARK: Supersets

const joinerTokens = ["supersetted with", "superset with", "s/s", "ss", "w/", "/"];
const countedJoinerTokens = ["then", "and"];
const specAfterJoiner = /^\p{Nd}+\p{White_Space}*\//u;

export function supersetParts(body: string, known: readonly string[]): string[] {
  const cs = chars(body);
  const masked = maskBrackets(cs);
  const parts: string[] = [];
  const strict: boolean[] = [false];
  let start = 0;
  let index = 0;
  while (index < masked.length) {
    const joiner = joinerEnd(index, masked);
    if (joiner === null) {
      index += 1;
      continue;
    }
    parts.push(cs.slice(start, index).join(""));
    strict.push(joiner.strict);
    start = joiner.end;
    index = joiner.end;
  }
  if (parts.length === 0) return [body];
  parts.push(cs.slice(start).join(""));

  const trimmed = parts.map(trimWS);
  if (trimmed.slice(1).some((part) => specAfterJoiner.test(part))) return [body];
  return isSuperset(trimmed, strict, known) ? trimmed : [body];
}

function maskBrackets(cs: string[]): string[] {
  const masked = cs.slice();
  let depth = 0;
  cs.forEach((c, index) => {
    if (c === "(" || c === "[") {
      depth += 1;
      masked[index] = "\u0001";
    } else if (c === ")" || c === "]") {
      depth = Math.max(0, depth - 1);
      masked[index] = "\u0001";
    } else if (depth > 0) {
      masked[index] = "\u0001";
    }
  });
  return masked;
}

function joinerEnd(index: number, cs: string[]): { end: number; strict: boolean } | null {
  if (cs[index] === "+") return { end: index + 1, strict: false };
  if (cs[index] === ",") return { end: index + 1, strict: true };
  if (!(index === 0 || isWhitespace(cs[index - 1]))) return null;
  for (const token of joinerTokens) {
    const end = matchToken(token, index, cs);
    if (end === null) continue;
    if (end === cs.length || isWhitespace(cs[end])) return { end, strict: false };
  }
  for (const token of countedJoinerTokens) {
    const end = matchToken(token, index, cs);
    if (end === null) continue;
    if (end === cs.length || isWhitespace(cs[end])) return { end, strict: true };
  }
  return null;
}

function matchToken(token: string, index: number, cs: string[]): number | null {
  let cursor = index;
  for (const expected of token) {
    if (expected === " ") {
      if (!(cursor < cs.length && isWhitespace(cs[cursor]))) return null;
      while (cursor < cs.length && isWhitespace(cs[cursor])) cursor += 1;
      continue;
    }
    if (!(cursor < cs.length && cs[cursor]!.toLowerCase() === expected)) return null;
    cursor += 1;
  }
  return cursor;
}

function isSuperset(parts: string[], strict: boolean[], known: readonly string[]): boolean {
  if (!(parts.length > 1 && parts.every((p) => p !== ""))) return false;
  const found = parts.map((p) => member(p, null, known)).filter((m): m is Member => m !== null);
  if (found.length !== parts.length) return false;
  if (!found.some((m) => m.sets !== null || m.exercise.target !== null)) return false;

  for (let index = 1; index < parts.length; index += 1) {
    const part = parts[index]!;
    const m = found[index]!;
    const counted = isNumber(first(part));
    if (index < strict.length && strict[index]) {
      if (!(counted && isNumber(first(parts[index - 1]!)))) return false;
      continue;
    }
    if (!(counted || isKnownName(m.exercise.name, known))) return false;
  }

  for (let i = 0; i + 1 < found.length; i += 1) {
    const lhs = found[i]!.exercise.name;
    const rhs = found[i + 1]!.exercise.name;
    if (isKnownName(`${lhs} and ${rhs}`, known) || isKnownName(`${lhs} ${rhs}`, known)) return false;
  }
  return true;
}

// MARK: Session headings (PlanSection)

const sessionWords = new Set(["day", "session", "workout", "weights", "gym", "lift", "training"]);

function isSessionLabel(body: string): boolean {
  const words = splitOmitting(body, isWhitespace);
  if (words.length < 2) return false;
  if (!sessionWords.has(chars(words[0]!.toLowerCase()).filter(isLetter).join(""))) return false;
  const number = trimSet(words[1]!, ":-–—.)").toLowerCase();
  if (number === "") return false;
  return chars(number).every(isNumber) || (count(number) === 1 && isLetter(number));
}

export function isSessionHeading(line: string, now: number, timeZone: string): boolean {
  const { body, hadBullet } = stripListMarker(trimWS(line));
  if (hadBullet || body === "") return false;
  if (label(body) !== null || supersetHeader(body) !== null || header(body) !== null) return false;
  const found = member(body, null, []);
  if (found !== null && (found.sets !== null || found.exercise.target !== null)) return false;
  if (isSessionLabel(body)) return true;
  return dayIn(body, now, now, timeZone) !== null;
}
